/**
 * autosaveScheduler — the "when to save" state machine for cloud files.
 *
 * Pure scheduling logic with three responsibilities and nothing else:
 *  - debounce: save ~`idleMs` after the editing pauses;
 *  - max-wait: while editing continuously, force a save at least every
 *    `maxWaitMs` so long sessions still persist;
 *  - single-flight: never run two saves for the same file at once; a change
 *    made while a save is in flight is coalesced into exactly one follow-up.
 *
 * Timing uses the global timers so it is fully controllable under vitest fake
 * timers — no hidden `setTimeout` webs, no untestable sequencing. Retry/backoff
 * and status/version tracking deliberately live in the caller (the hook), not
 * here; `onFlush` is the single injected side effect.
 */

export const DEFAULT_IDLE_MS = 2000;
export const DEFAULT_MAX_WAIT_MS = 15000;

export interface CreateAutosaveSchedulerOptions {
  /**
   * Performs the actual save. Resolve on success (a pending change may then be
   * coalesced into an immediate follow-up); reject on failure so the scheduler
   * does not tight-loop retrying — the caller owns retry/backoff.
   */
  onFlush: () => Promise<void>;
  /** Idle debounce before saving after edits pause. */
  idleMs?: number;
  /** Hard ceiling between saves while editing never pauses. */
  maxWaitMs?: number;
}

export interface AutosaveScheduler {
  /** Record that the scene changed and (re)arm the timers. */
  notifyChange: () => void;
  /**
   * Mark work as pending WITHOUT arming the debounce timers. Used to keep a
   * failed/offline save eligible for a `flushNow` retry (driven by the caller's
   * backoff / reconnect), without treating it as fresh editing activity.
   */
  markDirty: () => void;
  /** Save any pending changes right now (e.g. on unload / reconnect). */
  flushNow: () => void;
  /** Drop pending changes and clear timers (e.g. on unmount / file switch). */
  cancel: () => void;
  isDirty: () => boolean;
  isSaving: () => boolean;
}

export const createAutosaveScheduler = (
  opts: CreateAutosaveSchedulerOptions,
): AutosaveScheduler => {
  const idleMs = opts.idleMs ?? DEFAULT_IDLE_MS;
  const maxWaitMs = opts.maxWaitMs ?? DEFAULT_MAX_WAIT_MS;

  let dirty = false;
  let saving = false;
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  let maxTimer: ReturnType<typeof setTimeout> | null = null;

  const clearTimers = () => {
    if (idleTimer !== null) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }
    if (maxTimer !== null) {
      clearTimeout(maxTimer);
      maxTimer = null;
    }
  };

  const run = () => {
    clearTimers();
    if (saving || !dirty) {
      // If a save is in flight we leave `dirty` set; the in-flight save's
      // completion re-checks it, so the change is coalesced into one follow-up.
      return;
    }
    dirty = false;
    saving = true;
    opts
      .onFlush()
      .then(() => {
        saving = false;
        if (dirty) {
          // A change arrived while we were saving — flush it now (single-flight).
          run();
        }
      })
      .catch(() => {
        // The flush failed (e.g. offline). Do NOT re-run through the coalescing
        // path even if the caller marked work dirty for a retry — that would be
        // a tight loop that defeats the caller's exponential backoff. Retries
        // are driven exclusively by the caller (backoff timer / reconnect via
        // `flushNow`); genuine new edits still re-arm the debounce via
        // `notifyChange`.
        saving = false;
      });
  };

  const notifyChange = () => {
    dirty = true;
    if (idleTimer !== null) {
      clearTimeout(idleTimer);
    }
    idleTimer = setTimeout(run, idleMs);
    if (maxTimer === null) {
      // Not reset on subsequent changes: continuous editing still hits the cap.
      maxTimer = setTimeout(run, maxWaitMs);
    }
  };

  return {
    notifyChange,
    markDirty: () => {
      dirty = true;
    },
    flushNow: run,
    cancel: () => {
      clearTimers();
      dirty = false;
    },
    isDirty: () => dirty,
    isSaving: () => saving,
  };
};
